import type { Chat, Message } from "../data/demo";
import type { ConversationRow, EventDto, MessageDto } from "./types";
import type { ConversationNotice } from "../composables/chatTimeline";
import {
  EXPLAINED_SEND_ERRORS,
  canRetrySend,
  formatFileSize,
} from "@desfecha/contracts";
import { locale, localized, t, type MessageKey } from "../i18n";
import { profileTone } from "../composables/profileColors";
import { hoursText } from "../composables/policies";

export function timeLabel(iso: string | null, now = new Date()): string {
  if (!iso) return "";
  const d = new Date(iso);
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay)
    return d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return t("time.yesterday");
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

export function remaining(iso: string | null, now = new Date()): string | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - now.getTime();
  if (ms <= 0) return null;
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return t("time.minutes", { n: minutes });
  return t("time.hours", { n: Math.floor(minutes / 60) });
}

export function toChat(row: ConversationRow): Chat & { row: ConversationRow } {
  return {
    id: row.id,
    name: row.contact.name,
    preview: previewOf(row),
    time: timeLabel(row.lastMessageAt),
    unread: row.unreadCount,
    tone: profileTone(row.contact.address, row.contact.id),
    messages: [],
    row,
  };
}

function previewOf(row: ConversationRow): string {
  if (!row.lastMessage) return "";
  if (row.lastMessage.body) return row.lastMessage.body;
  const type = row.lastMessage.type ?? "text";
  return ["image", "audio", "video", "document", "sticker"].includes(type)
    ? mediaLabel(type)
    : type === "template"
      ? t("message.template")
      : "";
}

function formatBytes(bytes: number): string {
  return formatFileSize(bytes, locale.value);
}

interface SharedContact {
  name?:
    | string
    | {
        formattedName?: string;
        firstName?: string;
        formatted_name?: string;
        first_name?: string;
      };
  phones?: Array<{ phone?: string; waId?: string; wa_id?: string }>;
}

/** Contact cards, received or sent (canonical camelCase; raw WhatsApp snake_case tolerated). */
function sharedContacts(
  content: Record<string, unknown>,
): Array<{ name: string; phones: string[] }> {
  const list = (content.contacts ?? []) as SharedContact[];
  return list.map((c) => ({
    name:
      typeof c.name === "string"
        ? c.name
        : (c.name?.formattedName ??
          c.name?.formatted_name ??
          c.name?.firstName ??
          c.name?.first_name ??
          ""),
    phones: (c.phones ?? [])
      .map((p) => {
        const wa = p.waId ?? p.wa_id;
        return p.phone ?? (wa ? `+${wa}` : "");
      })
      .filter(Boolean),
  }));
}
function formatDuration(ms: number): string {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
/** A message type's name, for what has no text (a photo, a file). */
export function mediaLabel(type: string): string {
  switch (type) {
    case "image":
      return t("message.media.image");
    case "audio":
      return t("message.media.audio");
    case "video":
      return t("message.media.video");
    case "document":
      return t("message.media.document");
    case "sticker":
      return t("message.media.sticker");
    default:
      return type;
  }
}

/** What a quote shows for a message: its text, or a label for media without a caption. */
export function quoteText(m: Message): string {
  if (m.text) return m.text;
  switch (m.kind) {
    case "image":
      return t("message.quote.photo");
    case "video":
      return t("message.quote.video");
    case "audio":
      return m.voice ? t("message.quote.voice") : t("message.quote.audio");
    case "file":
      return m.name ?? t("message.media.document");
    case "sticker":
      return t("message.quote.sticker");
    case "contact":
      return m.contacts?.[0]?.name ?? t("message.quote.contact");
    default:
      return "";
  }
}

/**
 * Fills each quoting message with what WhatsApp shows in the quote: author ("Você" or the
 * contact), text or media label, and a thumbnail for photos and videos.
 */
/**
 * Marks, on each list message the business sent, the row the contact chose: their answer
 * quotes the list message and carries the option (by id, or by its title).
 */
/** A closing's outcome by its name (sent with the event), never its internal code. */
export function outcomeName(d: Record<string, unknown>): string {
  const label = d.outcomeLabel as Record<string, string> | string | undefined;
  return label ? localized(label) : localized(String(d.outcome));
}

export function markChoices(list: Message[]): Message[] {
  const answers = new Map<string, NonNullable<Message["chosen"]>>();
  for (const m of list)
    if (!m.out && m.chosen && m.replyToProvider)
      answers.set(m.replyToProvider, m.chosen);
  if (!answers.size) return list;
  return list.map((m) => {
    const chosen = m.providerMessageId
      ? answers.get(m.providerMessageId)
      : undefined;
    if (!m.out || !chosen || !m.rich?.list) return m;
    return { ...m, rich: { ...m.rich, list: { ...m.rich.list, chosen } } };
  });
}

export function resolveQuotes(
  list: Message[],
  contactName: string,
  you: string,
): Message[] {
  const byProvider = new Map<string, Message>();
  for (const m of list)
    if (m.providerMessageId) byProvider.set(m.providerMessageId, m);
  return list.map((m) => {
    if (!m.replyToProvider) return m;
    const q = byProvider.get(m.replyToProvider);
    // Not loaded (older than this page): a reply usually quotes the other side.
    if (!q)
      return {
        ...m,
        reply: t("message.quote_unavailable"),
        replyAuthor: m.out ? contactName : you,
        replyOut: !m.out,
      };
    return {
      ...m,
      reply: quoteText(q),
      replyAuthor: q.out ? you : contactName,
      replyOut: q.out,
      replyId: q.id,
      ...((q.kind === "image" || q.kind === "video") && q.src
        ? { replyThumb: q.src }
        : {}),
    };
  });
}

/** Why a send failed, in the member's language; Meta's raw text only when the code is unknown. */
export function sendFailureText(
  code: string | null | undefined,
  detail?: string | null,
): string {
  if (code && EXPLAINED_SEND_ERRORS.includes(code))
    return t(("delivery.error." + code) as never);
  return detail || t("message.failed");
}

/** The buttons WhatsApp draws for each kind of interactive message, as they were sent. */
function interactiveButtons(i: {
  kind: string;
  options?: Array<{ id?: string; title: string; description?: string }>;
  url?: string;
  flow?: { cta: string };
}): Array<{ type: string; text: string }> {
  switch (i.kind) {
    case "buttons":
      return (i.options ?? []).map((o) => ({
        type: "quick_reply",
        text: o.title.slice(0, 20),
      }));
    case "list":
      return [{ type: "list", text: (i.url ?? "Opções").slice(0, 20) }];
    case "cta_url":
      return [
        { type: "url", text: (i.options?.[0]?.title ?? "Abrir").slice(0, 20) },
      ];
    case "flow":
      return i.flow ? [{ type: "flow", text: i.flow.cta.slice(0, 30) }] : [];
    default:
      return [];
  }
}

function richParts(
  m: MessageDto,
  content: {
    interactive?: Parameters<typeof interactiveButtons>[0] & {
      header?: string;
      footer?: string;
    };
  },
): Message["rich"] | null {
  if (m.type === "template" && m.template) {
    const { header, footer, buttons } = m.template;
    return header || footer || buttons.length
      ? { header, footer, buttons }
      : null;
  }
  const i = content.interactive;
  if (m.type === "interactive" && i)
    return {
      header: i.header ?? null,
      footer: i.footer ?? null,
      buttons: interactiveButtons(i),
      ...(i.kind === "list"
        ? {
            list: {
              title: (i.header ?? i.url ?? "Opções").slice(0, 24),
              options: (i.options ?? []).map((o) => ({
                ...(o.id ? { id: o.id } : {}),
                title: o.title.slice(0, 24),
                description: o.description?.slice(0, 72) ?? null,
              })),
            },
          }
        : {}),
    };
  return null;
}

export function toMessage(m: MessageDto): Message {
  const created = new Date(m.createdAt);
  const content = m.content as {
    media?: { filename?: string; caption?: string };
    template?: { name: string };
    selected?: { id?: string; title: string };
    reaction?: { emoji: string };
    location?: { name?: string; address?: string };
    replyTo?: { providerMessageId: string };
    linkPreview?: Message["linkPreview"];
    interactive?: {
      kind: string;
      header?: string;
      body?: string;
      footer?: string;
      options?: Array<{ id?: string; title: string; description?: string }>;
      url?: string;
      flow?: { cta: string };
    };
  };
  let text = m.body ?? "";
  switch (m.type) {
    case "image":
    case "audio":
    case "video":
    case "document":
    case "sticker": {
      const label = mediaLabel(m.type);
      const extra = content.media?.filename ?? content.media?.caption ?? "";
      text = extra ? `${label} · ${extra}` : label;
      // A file that never reached our storage (gone at Meta, or not kept by an imported platform).
      if (m.media?.status === "expired")
        text = `${text} · ${t("message.file_unavailable")}`;
      break;
    }
    default:
      break;
  }
  switch (m.type) {
    case "location":
      text = `${t("message.location")}${content.location?.name ? ` · ${content.location.name}` : ""}`;
      break;
    case "template":
      text =
        m.body ?? `${t("message.template")}: ${content.template?.name ?? ""}`;
      break;
    case "interactive":
      text = m.body ?? content.selected?.title ?? "";
      break;
    case "reaction":
      text = t("message.reaction", { emoji: content.reaction?.emoji ?? "" });
      break;
    default:
      break;
  }
  // Content removed by the workspace's retention policy or a contact's erasure (LGPD).
  const extra = (m.content ?? {}) as {
    purged?: boolean;
    edited?: boolean;
    sentFrom?: string;
    imported?: { from?: string };
    authorName?: string;
  };
  const purged = extra.purged === true;
  if (purged) text = t("message.purged");
  else if (m.status === "deleted" && !text) text = t("message.deleted_text");
  // Media rendered inline when the file is stored; otherwise the text label above is shown.
  const media =
    !purged && m.media && m.media.status === "stored" ? m.media : null;
  const mediaFields: Partial<Message> = media
    ? {
        kind:
          media.kind === "document" ? "file" : (media.kind as Message["kind"]),
        src: media.url,
        downloadUrl: `${media.url}?download=1`,
        name: media.filename ?? mediaLabel(media.kind),
        size: media.sizeBytes ? formatBytes(media.sizeBytes) : "",
        duration: media.durationMs ? formatDuration(media.durationMs) : "",
        ...(media.mime ? { mime: media.mime } : {}),
        ...(media.width ? { width: media.width } : {}),
        ...(media.height ? { height: media.height } : {}),
        ...(media.durationMs ? { durationMs: media.durationMs } : {}),
        ...(media.transcript ? { transcript: media.transcript } : {}),
        ...(media.transcriptStatus
          ? { transcriptStatus: media.transcriptStatus }
          : {}),
        // WhatsApp voice notes are OGG/Opus; ours carry the voice flag too.
        voice:
          media.kind === "audio" &&
          Boolean(
            media.voice ??
            (content.media as { voice?: boolean } | undefined)?.voice ??
            media.mime?.startsWith("audio/ogg"),
          ),
      }
    : {};
  const contacts =
    m.type === "contacts" && !purged
      ? sharedContacts(m.content as Record<string, unknown>)
      : null;
  // A template's media header keeps the template text under the picture.
  if (media)
    text = m.type === "template" ? (m.body ?? "") : (media.caption ?? "");
  const status: Message["status"] =
    m.status === "read"
      ? "read"
      : m.status === "delivered"
        ? "delivered"
        : "sent";
  if (contacts) text = "";
  // What a template or an interactive message showed around its text, as the contact sees it.
  const rich =
    !purged && m.direction === "outbound" ? richParts(m, content) : null;
  if (rich && m.type === "template" && m.template && !media)
    text = m.template.body;
  if (rich && m.type === "interactive" && content.interactive?.body)
    text = content.interactive.body;
  return {
    ...(rich ? { rich } : {}),
    id: m.id,
    text,
    ts: created.getTime(),
    ...mediaFields,
    ...(contacts
      ? { kind: "contact" as const, contacts, name: contacts[0]?.name ?? "" }
      : {}),
    ...(m.providerMessageId ? { providerMessageId: m.providerMessageId } : {}),
    out: m.direction === "outbound",
    time: created.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    }),
    date: localDay(created),
    ...(media || contacts ? {} : { kind: "text" as const }),
    status,
    ...(content.replyTo?.providerMessageId
      ? { replyToProvider: content.replyTo.providerMessageId }
      : {}),
    ...(m.direction === "inbound" && content.selected?.title
      ? {
          chosen: {
            id: content.selected.id ?? null,
            title: content.selected.title,
          },
        }
      : {}),
    ...(content.linkPreview && !purged && m.type === "text"
      ? { linkPreview: content.linkPreview }
      : {}),
    ...(m.sentAt ? { sentAt: m.sentAt } : {}),
    ...(m.deliveredAt ? { deliveredAt: m.deliveredAt } : {}),
    ...(m.readAt ? { readAt: m.readAt } : {}),
    ...(m.playedAt ? { playedAt: m.playedAt } : {}),
    ...(m.direction === "outbound" && (m.pricingCategory || m.metaCost)
      ? {
          pricing: {
            category: m.pricingCategory,
            type: m.pricingType ?? null,
            cost: m.metaCost ?? null,
          },
        }
      : {}),
    ...(m.status === "failed"
      ? {
          failed: sendFailureText(m.error?.code, m.error?.detail),
          ...(m.error?.code ? { errorCode: m.error.code } : {}),
          ...(m.error?.detail ? { errorDetail: m.error.detail } : {}),
          canRetry: m.direction === "outbound" && canRetrySend(m.error?.code),
        }
      : {}),
    ...(m.status === "queued" ? { queued: true } : {}),
    ...(m.status === "deleted" || purged ? { deleted: true } : {}),
    ...(extra.edited && m.status !== "deleted" ? { edited: true } : {}),
    // Sent from the WhatsApp Business app on the phone (coexistence).
    ...(extra.sentFrom === "business_app" ? { fromPhone: true } : {}),
    ...(extra.imported?.from ? { importedFrom: extra.imported.from } : {}),
    ...(extra.authorName ? { authorName: extra.authorName } : {}),
    // Written by an assistant: its "Dados da mensagem" show how it answered (decision 0035).
    ...(m.author?.kind === "assistant" ? { byAssistant: true } : {}),
  };
}

const TRANSFER_NOTES = [
  "gate_hold",
  "max_turns",
  "exit:field_set",
  "exit:keyword",
];

/**
 * The note after "Transferido": what a person wrote, as written; the assistant's and flows' own
 * reasons are codes, shown as words (a code like "ai_unavailable" means it could not go on).
 */
export function transferNote(note: unknown, actorKind: string): string {
  if (typeof note !== "string" || !note.trim()) return "";
  if (TRANSFER_NOTES.includes(note))
    return t(`event.transfer_note.${note.replace(":", "_")}` as MessageKey);
  if (actorKind !== "member" && /^[a-z]+(_[a-z0-9]+)+$/.test(note))
    return t("event.transfer_note.error");
  return note;
}

/** A tool event's line: who used which tool, what waits for whom, who decided. */
export function toolNotice(
  kind: string,
  d: Record<string, unknown>,
  memberName: (id: unknown) => string,
): { text: string; variant: "info" | "system" | "warning" } {
  const tool = String(d.toolName ?? "");
  const who = String(d.assistant ?? "") || t("event.actor.assistant");
  if (kind === "tool_pending")
    return {
      text: t(
        d.waitingFor === "person"
          ? "event.tool.waiting_person"
          : "event.tool.waiting_contact",
        { tool },
      ),
      variant: "warning",
    };
  if (kind === "tool_decided")
    return {
      text: t("event.tool.declined", {
        name: memberName(d.declinedBy ?? null) || t("message.someone"),
        tool,
      }),
      variant: "system",
    };
  if (d.status === "failed")
    return { text: t("event.tool.failed", { tool }), variant: "warning" };
  if (d.approvedBy)
    return {
      text: t("event.tool.approved", {
        tool,
        name: memberName(d.approvedBy) || t("message.someone"),
      }),
      variant: "system",
    };
  if (d.effect === "write")
    return {
      text: t(
        d.confirmedBy === "contact"
          ? "event.tool.write_confirmed"
          : "event.tool.write",
        { who, tool },
      ),
      variant: "system",
    };
  return { text: t("event.tool.read", { who, tool }), variant: "system" };
}

export function localDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** "sex., 10/10 14:30" in the agenda's time zone (the browser's when unknown). */
export function appointmentWhen(iso: string, timeZone: string): string {
  const at = new Date(iso);
  const opts: Intl.DateTimeFormatOptions = {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  };
  try {
    return at.toLocaleString(
      undefined,
      timeZone ? { ...opts, timeZone } : opts,
    );
  } catch {
    return at.toLocaleString(undefined, opts);
  }
}

/** "24/01/2027" from "2027-01-24". */
const day = (date: unknown) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(date ?? ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};

/** What a tapped button did, in words: "pausadas até 24/01/2027", "etiqueta adicionada". */
export function appliedParts(applied: unknown): string[] {
  const list = Array.isArray(applied)
    ? (applied as Array<Record<string, unknown>>)
    : [];
  return list.flatMap((a) => {
    switch (a.action) {
      case "pause":
        return [t("event.applied.pause", { day: day(a.until) })];
      case "opt_out":
        return [t("event.applied.opt_out")];
      case "opt_in":
        return [t("event.applied.opt_in")];
      case "route":
        return [t("event.applied.route")];
      case "close":
        return [t("event.applied.close")];
      case "tag_add":
        return [t("event.applied.tag_add")];
      case "tag_remove":
        return [t("event.applied.tag_remove")];
      case "field":
        return [t("event.applied.field", { field: String(a.key ?? "") })];
      default:
        return [];
    }
  });
}

/** "Leo pausou as mensagens ativas até 24/01/2027", "A pausa das mensagens ativas acabou". */
export function activeChangeText(
  actorKind: string,
  d: Record<string, unknown>,
  memberName: string,
): string {
  if (d.reason === "pause_ended") return t("event.active.pause_ended");
  const who =
    actorKind === "member"
      ? memberName || t("message.someone")
      : actorKind === "assistant"
        ? String(d.assistant ?? "") || t("event.actor.assistant")
        : t("event.active.integration");
  const status = String(d.status ?? "");
  if (status === "paused")
    return t("event.active.paused", { who, day: day(d.until) });
  if (status === "opted_out") return t("event.active.opted_out", { who });
  return t("event.active.resumed", { who });
}

/** "Concluída pela regra da campanha: 48 h sem resposta · desfecho escolhido pela IA" (decision 0044). */
export function closedRuleText(rule: unknown): string {
  if (!rule || typeof rule !== "object") return "";
  const r = rule as { level?: string; hours?: number; chosen?: boolean };
  if (!r.level || !r.hours) return "";
  return [
    t("event.closed_rule", {
      whose: t(`event.rule_whose.${r.level}` as MessageKey),
      hours: hoursText(r.hours),
    }),
    r.chosen ? t("event.closed_chosen") : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

/** Timeline notices from conversation events; rendered by ChatNotice between messages. */
export function toNotices(
  events: EventDto[],
  members: Array<{ id: string; name: string }>,
  statusLabel: (s: string) => string,
): ConversationNotice[] {
  const name = (id: unknown) => members.find((m) => m.id === id)?.name ?? "";
  return events
    .map((e, i): ConversationNotice | null => {
      const d = e.data;
      const date = localDay(new Date(e.occurred_at));
      const base = {
        id: `ev-${i}-${e.occurred_at}`,
        date,
        ts: new Date(e.occurred_at).getTime(),
        source: {
          kind: e.kind,
          actorKind: e.actor_kind,
          actorId: e.actor_id,
          data: d,
          at: e.occurred_at,
        },
      };
      switch (e.kind) {
        case "created":
          return {
            ...base,
            variant: "info",
            text: t("event.created"),
            icon: "chat",
          };
        case "assigned":
          return {
            ...base,
            variant: "system",
            // Assignments on a first reply keep the person as memberId.
            text: t("event.assigned", { name: name(d.to ?? d.memberId) }),
            icon: "user",
          };
        case "unassigned":
          return {
            ...base,
            variant: "system",
            text: t("event.unassigned"),
            icon: "list",
          };
        case "transferred":
          return {
            ...base,
            variant: "system",
            text: [t("event.transferred"), transferNote(d.note, e.actor_kind)]
              .filter(Boolean)
              .join(" · "),
            icon: "forward",
          };
        case "status_changed":
          return {
            ...base,
            variant: "system",
            text: t("event.status_changed", { to: statusLabel(String(d.to)) }),
          };
        case "snoozed":
          return {
            ...base,
            variant: "system",
            text: t("event.snoozed", { until: timeLabel(String(d.until)) }),
            icon: "clock",
          };
        case "closed":
          return {
            ...base,
            variant: "info",
            text: [
              t("event.closed"),
              d.outcome ? outcomeName(d) : "",
              closedRuleText(d.rule),
            ]
              .filter(Boolean)
              .join(" · "),
            icon: "checkCircle",
          };
        case "follow_up": {
          // The assistant woke on a quiet conversation (decision 0044): what it did.
          const result = String(d.result ?? "running");
          return {
            ...base,
            variant: result === "failed" ? "warning" : "system",
            text: t(
              ([
                "sent",
                "closed",
                "handed_off",
                "nothing",
                "superseded",
                "failed",
              ].includes(result)
                ? `event.follow_up.${result}`
                : "event.follow_up.running") as MessageKey,
              { n: Number(d.n ?? 1), of: Number(d.of ?? 1) },
            ),
            icon: "refresh",
          };
        }
        case "close_scheduled":
          return {
            ...base,
            variant: "info",
            text: t("event.close_scheduled", {
              outcome: outcomeName(d),
              when: d.at
                ? new Date(String(d.at)).toLocaleString(locale.value, {
                    day: "2-digit",
                    month: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "",
            }),
            icon: "clock",
          };
        case "close_unscheduled":
          return {
            ...base,
            variant: "info",
            text:
              d.reason === "contact_replied"
                ? t("event.close_unscheduled.contact_replied")
                : d.reason === "failed"
                  ? t("event.close_unscheduled.failed")
                  : t("event.close_unscheduled.cancelled", {
                      name: name(e.actor_id),
                    }),
            icon: "clock",
          };
        case "reopened":
          return {
            ...base,
            variant: "info",
            text: t("event.reopened"),
            icon: "refresh",
          };
        case "note_added":
          return {
            ...base,
            variant: "system",
            text: t("event.note_added"),
            icon: "edit",
          };
        case "priority_changed":
          return {
            ...base,
            variant: "warning",
            text: t("event.priority_changed", { to: String(d.to) }),
          };
        case "handler_changed":
          // Someone from the team wrote while the flow or the assistant was answering: it paused.
          if (
            d.reason === "member_reply" &&
            (d.from === "flow" || d.from === "assistant")
          )
            return {
              ...base,
              variant: "system",
              text: t(`event.taken_over.${d.from}`, { name: name(e.actor_id) }),
            };
          return {
            ...base,
            variant: "system",
            text: t("event.handler_changed", {
              to: t(`handler.${String(d.to)}` as MessageKey),
            }),
          };
        case "tool_called":
        case "tool_pending":
        case "tool_decided": {
          // The assistant's tools (decision 0033): what it looked up or changed, and what waits.
          const notice = toolNotice(e.kind, d, name);
          return { ...base, ...notice, icon: "workflow" };
        }
        case "cleared":
          // "Limpar conversa": who cleared it; what was there stays for who may see removed messages.
          return {
            ...base,
            variant: "system",
            text: t("event.cleared", {
              name: name(e.actor_id) || t("message.someone"),
            }),
            icon: "trash",
          };
        case "button_tapped": {
          // A template button and what it did (decision 0036).
          const parts = appliedParts(d.applied);
          return {
            ...base,
            variant: "system",
            text: [
              t("event.button_tapped", { button: String(d.button ?? "") }),
              ...parts,
            ].join(" · "),
            icon: "workflow",
          };
        }
        case "active_messages_changed":
          return {
            ...base,
            variant: d.status === "can_receive" ? "system" : "warning",
            text: activeChangeText(e.actor_kind, d, name(e.actor_id)),
            icon: "pause",
          };
        case "skill_opened":
          // The assistant opened one of its skills (decision 0035); the company can hide these.
          return {
            ...base,
            variant: "system",
            text: t("event.skill_opened", { name: String(d.name ?? "") }),
            icon: "star",
          };
        case "gate_held": {
          // The outbound review stopped an automated message; the person sees what was held.
          const draft = String(d.draft ?? "");
          return {
            ...base,
            variant: "warning",
            text: t(
              e.actor_kind === "flow"
                ? "event.gate_held_flow"
                : "event.gate_held_assistant",
              {
                draft: draft.length > 160 ? `${draft.slice(0, 157)}...` : draft,
              },
            ),
            icon: "shield",
          };
        }
        case "appointment_booked":
        case "appointment_rescheduled":
        case "appointment_cancelled":
        case "appointment_confirmed":
        case "appointment_attended":
        case "appointment_no_show":
        case "appointment_unconfirmed":
        case "appointment_change_requested":
        case "appointment_calendar_changed": {
          // Scheduling (decision 0022): what happened to the contact's appointment, in a line.
          const when = d.startsAt
            ? appointmentWhen(String(d.startsAt), String(d.timezone ?? ""))
            : "";
          const vars = {
            type: String(d.typeName ?? ""),
            when,
            name: String(d.memberName ?? ""),
          };
          const warn = [
            "appointment_unconfirmed",
            "appointment_change_requested",
            "appointment_calendar_changed",
            "appointment_no_show",
          ].includes(e.kind);
          const key =
            e.kind === "appointment_booked" && d.memberName
              ? "event.appointment_booked_with"
              : e.kind === "appointment_change_requested"
                ? d.request === "cancel"
                  ? "event.appointment_change_requested_cancel"
                  : "event.appointment_change_requested_move"
                : e.kind === "appointment_calendar_changed"
                  ? d.asked
                    ? "event.appointment_calendar_changed_asked"
                    : "event.appointment_calendar_changed"
                  : `event.${e.kind}`;
          return {
            ...base,
            variant: warn ? "warning" : "info",
            text: t(key as "event.appointment_booked", vars),
            icon: "calendar",
          };
        }
        case "outcome_set":
          return null;
        default:
          return e.actor_kind === "policy"
            ? { ...base, variant: "system", text: t("event.policy") }
            : null;
      }
    })
    .filter((n): n is ConversationNotice => n !== null);
}
